/* eslint-disable class-methods-use-this */
/**
 * Luma Group
 *
 * @author Robuust
 * @author Jurriën Peters <jurrien@robuust.digital>
 */

import { Controller } from '@hotwired/stimulus';
import { load } from '@apple/mapkit-loader';

let mapKitPromise;

function loadMapKit() {
  mapKitPromise ??= load({
    version: '6',
    token: import.meta.env.VITE_MAPKIT_TOKEN,
    libraries: ['services', 'full-map'],
  });

  return mapKitPromise;
}

/**
 * Controller for jobs.
 */
export default class extends Controller {
  /**
   * @return {Array}
   */
  static targets = ['list', 'map', 'postcode', 'alert', 'alertFields'];

  /**
  * @return {Array}
  */
  static classes = ['active'];

  /**
   * @return {Object}
   */
  static values = {
    url: String,
  };

  /**
   * @var {Map}
   */
  map;

  /**
   * @var {Map}
   */
  annotations = new Map();

  /**
   * @var {Map}
   */
  circleOverlays = new Map();

  /**
   * @var {MapKit}
   */
  mapkit;

  /**
   * Connect map.
   *
   * @param {HTMLElement} target
   */
  async mapTargetConnected(target) {
    this.mapkit = await loadMapKit();
    this.map = new this.mapkit.Map(target);

    // Always set the map's region to The Netherlands
    const region = new this.mapkit.CoordinateRegion(
      new this.mapkit.Coordinate(52.1326, 5.2913),
      new this.mapkit.CoordinateSpan(4.5, 4.5),
    );
    this.map.region = region;

    // Get markers
    const response = await fetch(this.urlValue);
    const result = await response.json();

    // Set markers
    const markers = [];
    for (let i = 0; i < result.length; i += 1) {
      const job = result[i];
      const coords = new this.mapkit.Coordinate(
        job.clusterLatitude ?? job.latitude,
        job.clusterLongitude ?? job.longitude,
      );

      const annotation = this.getAnnotation(coords, [job], {
        data: { ...job },
        anchorOffset: new DOMPoint(0, -8),
      });

      markers.push(annotation);
    }

    this.map.annotationForCluster = (clusterAnnotation) => this.getAnnotation(
      clusterAnnotation.coordinate,
      clusterAnnotation.memberAnnotations,
      { anchorOffset: new DOMPoint(0, -16) },
    );

    this.map.showItems(markers, {
      minimumSpan: new this.mapkit.CoordinateSpan(0.5, 0.5),
    });
  }

  /**
   * Destroy map.
   */
  mapTargetDisconnected() {
    if (this.map !== undefined) {
      this.map.destroy();
    }
  }

  /**
   * Request submit.
   *
   * @param {Event} e
   */
  submit(e) {
    e.target.form.requestSubmit();
  }

  /**
   * Geocode.
   *
   * @param {Event} e
   */
  async geocode(e) {
    // Add pulse animation
    e.target.classList.add('animate-pulse');

    // Get current GPS position
    const position = await this.getPosition();
    this.mapkit ??= await loadMapKit();

    // Send coords to server to retrieve current country
    const coordinates = new this.mapkit.Coordinate(
      position.coords.latitude,
      position.coords.longitude,
    );
    const address = await this.getAddress(coordinates);

    // Set postcode in postcode field
    this.postcodeTarget.value = address?.results[0]?.postCode;

    // Remove pulse animation
    e.target.classList.remove('animate-pulse');
  }

  /**
   * Get position.
   *
   * @return {Promise}
   */
  getPosition() {
    return new Promise((resolve, reject) => {
      navigator.geolocation.getCurrentPosition(resolve, reject);
    });
  }

  /**
   * Get address from coordinates.
   *
   * @param {Coordinate} coordinates
   *
   * @return {Promise}
   */
  getAddress(coordinates) {
    const geocoder = new this.mapkit.Geocoder();
    return geocoder.reverseLookup(coordinates);
  }

  /**
   * Create annotation.
   *
   * @param {Coordinate} coords
   * @param {Array} markers
   * @param {Object} options
   *
   * @returns {Annotation}
   */
  getAnnotation(coordinate, markers, options = {}) {
    const key = `${coordinate.latitude.toFixed(7)},${coordinate.longitude.toFixed(7)}`;
    const annotation = new this.mapkit.Annotation(
      coordinate,
      this.getMarker.bind(this, markers),
      {
        animates: false,
        clusteringIdentifier: key,
        ...options,
        callout: { calloutElementForAnnotation: this.getCallout.bind(this) },
      },
    );

    if (this.hasActiveClass) {
      annotation.addEventListener('select', () => {
        annotation.element.classList.add(this.activeClass);

        // Convert the annotation's coordinate to a point in the map's view.
        const annotationPoint = this.map.convertCoordinateToPointOnPage(annotation.coordinate);

        // Adjust the point by subtracting 150 from the y-coordinate.
        // This moves the center upward, so the annotation appears lower in the view.
        const adjustedPoint = new DOMPoint(annotationPoint.x, annotationPoint.y - 150);

        // Convert the adjusted point back into a geographic coordinate.
        const newCenterCoordinate = this.map.convertPointOnPageToCoordinate(adjustedPoint);

        // Animate to the new center coordinate.
        this.map.setCenterAnimated(newCenterCoordinate);
      });

      annotation.addEventListener('deselect', () => {
        annotation.element.classList.remove(this.activeClass);
      });
    }

    this.annotations.set(key, annotation);
    markers.forEach((marker) => {
      const data = marker.data ?? marker;
      const dataKey = `${data.latitude.toFixed(7)},${data.longitude.toFixed(7)}`;
      this.annotations.set(dataKey, annotation);
    });

    if (this.circleOverlays.has(key)) {
      this.map.removeOverlay(this.circleOverlays.get(key));
    }

    const radius = 2500 + (1 - Math.exp(-markers.length / 10)) * 5000;
    const circle = new this.mapkit.CircleOverlay(coordinate, radius);
    circle.style = new this.mapkit.Style({
      lineWidth: 0,
      fillColor: '#306852',
      fillOpacity: 0.3,
    });
    this.map.addOverlay(circle);
    this.circleOverlays.set(key, circle);

    return annotation;
  }

  /**
   * Create marker.
   *
   * @param {Array} jobs
   *
   * @return {Element}
   */
  getMarker(jobs) {
    const element = document.createElement('div');
    element.className = 'job-marker';

    if (jobs.length > 1) {
      element.textContent = jobs.length;
    }

    return element;
  }

  /**
   * Create callout.
   *
   * @param {Object} annotation
   */
  getCallout(annotation) {
    let data = [annotation.data];
    if (annotation.memberAnnotations) {
      data = annotation.memberAnnotations.map((a) => a.data);
    }

    const url = new URL('/actions/jobs/jobs/callout', window.location.origin);
    url.search = window.location.search;
    data.forEach((job) => {
      url.searchParams.append('ids[]', job.id);
      url.searchParams.append(`locations[${job.id}][]`, job.location);
    });
    url.searchParams.append('lat', annotation.coordinate.latitude);
    url.searchParams.append('lng', annotation.coordinate.longitude);

    const element = document.createElement('turbo-frame');
    element.id = 'callout';
    element.src = url.toString();

    return element;
  }

  /**
   * Hover target class.
   *
   * @param {Event} e
   */
  hover(e) {
    const target = e.target.closest('[data-keys]');
    const active = e.type === 'mouseenter' || target.classList.contains(this.activeClass);
    const keys = JSON.parse(target.dataset.keys);
    const activeAnnotations = new Set(keys.map((key) => this.annotations.get(key)));

    new Set(this.annotations.values()).forEach((annotation) => {
      annotation.element.classList.toggle(
        this.activeClass,
        activeAnnotations.has(annotation) && active,
      );
    });
  }

  /**
   * Subscribe for job alert.
   *
   * @param {Event} e
   */
  async alert(e) {
    e.preventDefault();

    const { target } = e;
    const response = await fetch(target.action, {
      method: target.method.toUpperCase(),
      credentials: 'include',
      headers: {
        Accept: 'application/json',
      },
      body: new FormData(target),
    });
    const result = await response.json().catch(() => null);

    if (result?.success) {
      window.dataLayer?.push({
        event: 'vacature_alert_verstuurd',
      });

      this.alertTarget.innerHTML = '<span class="font-medium">Vacature-alert ingesteld!</span><br>Afmelden kan via de link onderaan de e-mail.';
      this.alertFieldsTarget.hidden = true;
    }
  }

  /**
   * Scroll list and window to top.
   */
  scrollToTop() {
    const scrollConfig = { top: 0, behavior: 'smooth' };
    this.listTarget.scrollTo(scrollConfig);
    window.scrollTo(scrollConfig);
  }
}
